/**
 * CurrentTemplateContext
 * Stores the active template name (BaseTemplate | BioLinkTemplate | ArgentinaBioTemplate | MaintenanceTemplate)
 * for PostHog pageview/route_change when on "/". Ensures PostHog + session replay
 * can be filtered by template for all templates.
 */

import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

type TemplateName = "BaseTemplate" | "BioLinkTemplate" | "ArgentinaBioTemplate" | "MaintenanceTemplate" | null;

interface CurrentTemplateContextValue {
  templateName: TemplateName;
  setTemplateName: (name: string | null) => void;
}

const CurrentTemplateContext = createContext<CurrentTemplateContextValue | null>(null);

const VALID_TEMPLATES: TemplateName[] = ["BaseTemplate", "BioLinkTemplate", "ArgentinaBioTemplate", "MaintenanceTemplate"];

export function CurrentTemplateProvider({ children }: { children: ReactNode }) {
  const [templateName, setTemplateNameState] = useState<TemplateName>(null);
  const setTemplateName = useCallback((name: string | null) => {
    setTemplateNameState(
      name && VALID_TEMPLATES.includes(name as TemplateName) ? (name as TemplateName) : null
    );
  }, []);

  return (
    <CurrentTemplateContext.Provider value={{ templateName, setTemplateName }}>
      {children}
    </CurrentTemplateContext.Provider>
  );
}

export function useCurrentTemplate(): CurrentTemplateContextValue {
  const ctx = useContext(CurrentTemplateContext);
  if (!ctx) {
    return {
      templateName: null,
      setTemplateName: (_: string | null) => {},
    };
  }
  return ctx;
}
